import { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Stack,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import {
  useGetSections,
  useAddSection,
  useUpdateSection,
  useDeleteSections,
} from "api/management/sections";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";

import { SectionDialog, ISection } from "./parts";
import {
  ConfirmationDialog,
  MessageDialog,
  ProgressBackdropDialog,
} from "components/parts/dialogs";
import { useI18n } from "i18n";

const Page = () => {
  const { t } = useI18n();
  const { loading: getLoading, service: getSections } = useGetSections();
  const { loading: addLoading, service: addSection } = useAddSection();
  const { loading: updateLoading, service: updateSection } = useUpdateSection();
  const { loading: deleteLoading, service: deleteSections } =
    useDeleteSections();

  const [sections, setSections] = useState<ISection[]>([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openNoticeDialog, setOpenNoticeDialog] = useState(false);
  const [noticeMessage, setNoticeMessage] = useState<string>("");

  const [editData, setEditData] = useState<ISection | null>(null);
  const [editType, setEditType] = useState<"edit" | "add" | "none">("none");
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const handleEditClick = (row) => {
    setEditType("edit");
    setEditData(row);
    setOpenEditDialog(true);
  };

  const handleAddClick = () => {
    setEditType("add");
    setEditData(null);
    setOpenEditDialog(true);
  };

  const handleDeleteClick = () => {
    if (rowSelectionModel.length === 0) {
      setNoticeMessage(t("sections.selectToDelete"));
      setOpenNoticeDialog(true);
      return;
    }

    setOpenDeleteDialog(true);
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: t("field.id"), width: 90, hideable: false },
    {
      field: "name",
      headerName: t("field.sectionName"),
      width: 150,
      editable: false,
    },
    {
      field: "actions",
      headerName: t("common.actions"),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton color="primary" onClick={() => handleEditClick(params.row)}>
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  useEffect(() => {
    getSections()
      .then((response) => {
        const { data: result } = response;
        return result;
      })
      .then((result) => {
        const { data } = result;
        setSections(data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{ mr: 1 }}
          >
            {t("common.add")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
          >
            {t("common.delete")}
          </Button>
        </Stack>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            loading={getLoading}
            rows={sections}
            rowCount={sections.length}
            columns={columns}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            rowSelectionModel={rowSelectionModel}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
            }}
            sx={{ minHeight: 400 }}
            pageSizeOptions={[10, 20, 50]}
            checkboxSelection
            disableRowSelectionOnClick
          />
        </Box>
      </Container>
      <SectionDialog
        open={openEditDialog}
        editType={editType}
        editData={editData}
        handleClose={() => {
          setEditData(null);
          setOpenEditDialog(false);
        }}
        handleSubmit={async (data) => {
          if (editType === "add") {
            // 追加処理
            const resultData = await addSection(data).then((response) => {
              const { data: result } = response;
              return result["data"];
            });

            setSections([...sections, resultData]);
          } else if (editType === "edit") {
            // 編集処理
            const resultData = await updateSection(data).then((response) => {
              const { data: result } = response;
              return result["data"];
            });
            setSections((prevRows) => {
              return prevRows.map((row) => {
                const prevId = row["id"];
                return prevId === resultData["id"]
                  ? {
                      ...row,
                      name: resultData["name"],
                    }
                  : row;
              });
            });
          }
          setOpenEditDialog(false);
          return true;
        }}
      />

      <ConfirmationDialog
        title={t("sections.deleteTitle")}
        message={t("sections.deleteMessage")}
        negativeText={t("common.cancel")}
        positiveText={t("common.delete")}
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        handleSubmit={() => {
          const idArray = rowSelectionModel;
          deleteSections(idArray)
            .then(() => {
              // 選択された行のIDに基づいて行を一括削除
              setSections(
                sections.filter((row) => !rowSelectionModel.includes(row.id))
              );
              setRowSelectionModel([]); // 選択状態をクリア
              setOpenDeleteDialog(false);
            })
            .catch((error) => {
              console.log(error);
            });
        }}
      />
      <MessageDialog
        title={t("common.notice")}
        message={noticeMessage}
        open={openNoticeDialog}
        actionText={t("common.confirm")}
        handleClose={() => {
          setOpenNoticeDialog(false);
        }}
      />

      <ProgressBackdropDialog open={addLoading || updateLoading || deleteLoading} />
    </>
  );
};

export default Page;
